import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { Package, Clock, CheckCircle, ArrowRight, CalendarDays, Users } from 'lucide-react';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast } from '../services/toast';

const CHART_COLORS = [
  '#22C55E',
  '#8B5CF6',
  '#F49D37',
];

const useIsMobile = (breakpoint = 640) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

const StoreOverview = ({ store, profile, setActiveTab, storeId }) => {
  const isMobile = useIsMobile();
  const [drivers, setDrivers] = useState([]);
  const [allDonations, setAllDonations] = useState([]);
  const [dateRange, setDateRange] = useState(30);
  const [isLoadingDonations, setIsLoadingDonations] = useState(true);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);
  const [donationData, setDonationData] = useState({
    donationStatus: [],
    donationsPerDay: [],
    totalDonations: 0,
    pendingDonations: 0,
    assignedDonations: 0,
    completedDonations: 0,
    upcomingPickups: [],
  });

  useEffect(() => {
    const fetchDonationData = async () => {
      setIsLoadingDonations(true);
      try {
        const response = await ApiServices.getData(`/donations/${store.id}`);
        const donations = response.data;
        setAllDonations(donations);
      } catch (error) {
        ShowDangerToast('Failed to load donation data');
      } finally {
        setIsLoadingDonations(false);
      }
    };
    fetchDonationData();
  }, [store.id]);

  useEffect(() => {
    const donations = allDonations;
    const now = new Date();
    const rangeStart = new Date(now.getTime() - dateRange * 24 * 60 * 60 * 1000);

    const rangedDonations = donations.filter(d => {
      const date = new Date(d.pickupDate);
      return date >= rangeStart;
    });

    const statusCounts = rangedDonations.reduce(
      (acc, donation) => {
        if (donation.status === 'pending') acc.pending += 1;
        if (donation.status === 'assigned') acc.assigned += 1;
        if (donation.status === 'completed') acc.completed += 1;
        if (donation.status === 'rejected') acc.rejected += 1;
        return acc;
      },
      { pending: 0, assigned: 0, completed: 0, rejected: 0 }
    );

    const donationStatus = [
      { name: 'Completed', value: statusCounts.completed },
      { name: 'Ready', value: statusCounts.assigned },
      { name: 'Needs Review', value: statusCounts.pending },
    ].filter(status => status.value > 0);

    const dailyDonations = {};
    rangedDonations.forEach(donation => {
      const date = new Date(donation.pickupDate);
      const isoKey = date.toISOString().split('T')[0];
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dailyDonations[isoKey]) dailyDonations[isoKey] = { label, count: 0 };
      dailyDonations[isoKey].count += 1;
    });
    const donationsPerDay = Object.keys(dailyDonations)
      .sort()
      .map(key => ({
        name: dailyDonations[key].label,
        pickups: dailyDonations[key].count,
      }));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcomingPickups = donations
      .filter(d => {
        const pickupDate = new Date(d.pickupDate);
        return pickupDate >= today && (d.status === 'pending' || d.status === 'assigned');
      })
      .sort((a, b) => new Date(a.pickupDate) - new Date(b.pickupDate))
      .slice(0, 4)
      .map(d => ({
        id: d.id,
        donorName: d.donorName || d.donorEmail || 'Unknown',
        pickupDate: new Date(d.pickupDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        pickupTime: d.pickupTime || 'TBD',
        status: d.status,
        items: d.DonationItems?.length || 0,
      }));

    setDonationData({
      donationStatus,
      donationsPerDay,
      totalDonations: rangedDonations.length,
      pendingDonations: statusCounts.pending,
      assignedDonations: statusCounts.assigned,
      completedDonations: statusCounts.completed,
      upcomingPickups,
    });
  }, [allDonations, dateRange]);

  useEffect(() => {
    const fetchDrivers = async () => {
      setIsLoadingDrivers(true);
      try {
        const sid = storeId || store.id;
        const driversResponse = await ApiServices.getData(`/drivers?storeId=${sid}`);
        setDrivers(driversResponse.data || []);
      } catch (err) {
        console.error('Failed to load drivers for overview', err);
      } finally {
        setIsLoadingDrivers(false);
      }
    };
    fetchDrivers();
  }, [storeId, store.id]);

  const statCards = [
    {
      label: 'Total Pickups',
      value: donationData.totalDonations,
      icon: Package,
      ringColor: 'bg-navy-100 text-navy-600',
      filterValue: '',
    },
    {
      label: 'Needs Review',
      value: donationData.pendingDonations,
      icon: Clock,
      ringColor: 'bg-orange-100 text-orange-500',
      filterValue: 'pending',
    },
    {
      label: 'Ready to Assign',
      value: donationData.assignedDonations,
      icon: Clock,
      ringColor: 'bg-blue-100 text-blue-500',
      filterValue: 'assigned',
    },
    {
      label: 'Completed',
      value: donationData.completedDonations,
      icon: CheckCircle,
      ringColor: 'bg-green-100 text-green-500',
      filterValue: 'completed',
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-2.5 rounded-lg shadow-lg border border-gray-100">
          <p className="text-sm font-semibold text-navy-800">{label}</p>
          <p className="text-sm text-gray-600">{payload[0].value} pickups</p>
        </div>
      );
    }
    return null;
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-50 text-amber-700',
      assigned: 'bg-blue-50 text-blue-700',
      completed: 'bg-green-50 text-green-700',
    };
    const labels = { pending: 'Needs Review', assigned: 'Ready', completed: 'Completed' };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTodayDateStr = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split('T')[0];
  };

  const getDriverTodayPickups = (driverId) => {
    const todayStr = getTodayDateStr();

    const pickups = allDonations.filter(d =>
      d.driverId === driverId &&
      d.pickupDate === todayStr &&
      (d.status === 'assigned' || d.status === 'completed' || d.status === 'onGoing')
    );

    console.log(
      `Calculating today's pickups for driver ${driverId} on ${todayStr}. Result count: ${pickups.length}`
    );

    return pickups.length;
  };

  return (
    <div className="min-h-0 p-0 space-y-6">

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">Welcome back! Here's what's happening today.</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="self-start sm:self-auto px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-navy-700 cursor-pointer"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoadingDonations ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="h-4 w-20 bg-gray-200 rounded" />
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gray-200" />
              </div>
              <div className="h-8 w-16 bg-gray-200 rounded" />
            </div>
          ))
        ) : (
          statCards.map(stat => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                onClick={() => {
                  if (setActiveTab) {
                    setActiveTab('Donations', stat.filterValue);
                  }
                }}
                className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-navy-200 transition-all cursor-pointer active:scale-[0.98] group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs sm:text-sm font-medium text-gray-500 group-hover:text-navy-600 transition-colors">{stat.label}</span>
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full ${stat.ringColor} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-navy-800">{stat.value}</div>
              </div>
            );
          })
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base font-semibold text-navy-800 mb-4">Pickup Trends</h3>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
            <LineChart
              data={donationData.donationsPerDay}
              margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
            >
              <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="pickups"
                stroke="#2B3A67"
                strokeWidth={2.5}
                dot={{ stroke: '#2B3A67', fill: '#fff', strokeWidth: 2, r: 4 }}
                activeDot={{ stroke: '#2B3A67', fill: '#2B3A67', r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base font-semibold text-navy-800 mb-4">Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 320}>
            <PieChart>
              <Pie
                data={donationData.donationStatus}
                dataKey="value"
                outerRadius={isMobile ? 70 : 100}
                innerRadius={isMobile ? 38 : 55}
                paddingAngle={3}
                label={isMobile ? false : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {donationData.donationStatus.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span className="text-xs sm:text-sm text-gray-600">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-navy-800">Upcoming Pickups</h3>
            {setActiveTab && (
              <button
                onClick={() => setActiveTab('Calendar')}
                className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1 cursor-pointer bg-transparent border-none"
              >
                View Calendar <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {isLoadingDonations && (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-200" />
                    <div>
                      <div className="h-4 w-32 bg-gray-200 rounded mb-1.5" />
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                    </div>
                  </div>
                  <div className="h-5 w-16 bg-gray-200 rounded-full" />
                </div>
              ))
            )}
            {!isLoadingDonations && donationData.upcomingPickups.length === 0 && (
              <div className="px-5 py-8 text-center">
                <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No upcoming pickups</p>
              </div>
            )}
            {!isLoadingDonations && donationData.upcomingPickups.map(pickup => (
              <div key={pickup.id} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
                    <Package className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{pickup.donorName}</p>
                    <p className="text-xs text-gray-400">{pickup.pickupDate} · {pickup.pickupTime} · {pickup.items} item{pickup.items !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                {statusBadge(pickup.status)}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-navy-800">Driver Utilization</h3>
            {setActiveTab && (
              <button
                onClick={() => setActiveTab('Drivers')}
                className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1 cursor-pointer bg-transparent border-none"
              >
                Manage <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {isLoadingDrivers && (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-200" />
                    <div className="h-4 w-28 bg-gray-200 rounded" />
                  </div>
                  <div className="h-4 w-12 bg-gray-200 rounded" />
                </div>
              ))
            )}
            {!isLoadingDrivers && drivers.length === 0 && (
              <div className="px-5 py-8 text-center">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No drivers added yet</p>
              </div>
            )}
            {!isLoadingDrivers && drivers.slice(0, 5).map((driver, idx) => {
              const todayPickups = getDriverTodayPickups(driver.id);
              const maxLimit = driver.maxPickupsPerDay || 10;
              return (
                <div key={driver.id || idx} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-navy-100 text-navy-600 flex items-center justify-center flex-shrink-0 text-sm font-bold uppercase">
                      {driver.fullName ? driver.fullName.charAt(0) : 'D'}
                    </div>
                    <p className="text-sm font-medium text-gray-800 truncate">{driver.fullName || 'Unknown Driver'}</p>
                  </div>
                  <span className="text-sm text-gray-400 whitespace-nowrap">{todayPickups} / {maxLimit}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreOverview;